import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addNotification } from '../../store/slices/uiSlice';
import ConfirmModal from '../common/ConfirmModal';
import {
  createAdminNews,
  deleteAdminNews,
  fetchAdminNews,
  updateAdminNews,
} from '../../services/adminNewsService';

const emptyDraft = () => ({
  title: '',
  url: '',
  source: '',
  published: true,
});

const AdminNewsPanel = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canManage = user?.role === 'ADMIN' || user?.role === 'ORG_ADMIN';

  const load = async () => {
    try {
      setLoading(true);
      setItems(await fetchAdminNews());
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data?.error || error?.message || 'Failed to load news',
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canManage) load();
  }, [canManage]);

  const resetForm = () => {
    setDraft(emptyDraft());
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        url: draft.url.trim(),
        source: draft.source.trim(),
        published: Boolean(draft.published),
      };
      if (editingId) {
        const updated = await updateAdminNews(editingId, payload);
        setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        dispatch(addNotification({ type: 'success', message: 'News updated' }));
      } else {
        const created = await createAdminNews(payload);
        setItems((prev) => [created, ...prev]);
        dispatch(addNotification({ type: 'success', message: 'News published' }));
      }
      resetForm();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.message || 'Failed to save news',
      }));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const id = deleteTarget?.id;
    if (!id) return;
    setDeleteTarget(null);
    try {
      await deleteAdminNews(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      if (editingId === id) resetForm();
      dispatch(addNotification({ type: 'success', message: 'News removed' }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.message || 'Failed to delete news',
      }));
    }
  };

  if (!canManage) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">News</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Add a title and link. People will see it in the briefing under Today&apos;s news.
        {user?.role === 'ORG_ADMIN'
          ? ' This is shown to people in your organization.'
          : ' Platform news is shown to everyone.'}
      </p>

      <form onSubmit={handleSubmit} className="mt-5 grid grid-cols-1 gap-3 max-w-2xl">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Title</span>
          <input
            value={draft.title}
            onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))}
            maxLength={240}
            required
            placeholder="What should people read?"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Link</span>
          <input
            type="url"
            value={draft.url}
            onChange={(event) => setDraft((prev) => ({ ...prev, url: event.target.value }))}
            required
            placeholder="https://"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Source (optional)</span>
          <input
            value={draft.source}
            onChange={(event) => setDraft((prev) => ({ ...prev, source: event.target.value }))}
            maxLength={80}
            placeholder="Workloop"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
          />
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={draft.published}
            onChange={(event) => setDraft((prev) => ({ ...prev, published: event.target.checked }))}
          />
          Show in Today&apos;s news
        </label>
        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : (editingId ? 'Update news' : 'Add news')}
          </button>
          {editingId ? (
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
            >
              Cancel
            </button>
          ) : null}
        </div>
      </form>

      <div className="mt-8">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Published items
        </h3>
        {loading ? (
          <p className="mt-3 text-sm text-gray-500">Loading…</p>
        ) : items.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No news yet. Add a title and link above.</p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-700">
            {items.map((item) => (
              <li key={item.id} className="py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{item.title}</p>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-0.5 block text-xs text-primary-600 dark:text-primary-400 truncate hover:underline"
                  >
                    {item.url}
                  </a>
                  <p className="mt-1 text-[11px] text-gray-400">
                    {item.source || 'Workloop'}
                    {item.published ? '' : ' · Hidden'}
                  </p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(item.id);
                      setDraft({
                        title: item.title || '',
                        url: item.url || '',
                        source: item.source || '',
                        published: item.published !== false,
                      });
                    }}
                    className="text-xs font-semibold text-primary-600 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(item)}
                    className="text-xs font-semibold text-rose-600 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        type="danger"
        title="Delete news"
        message={`Remove “${deleteTarget?.title || 'this news'}”? People will no longer see it in Today’s news.`}
        confirmText="Delete"
        cancelText="Cancel"
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default AdminNewsPanel;
